import { type FormEvent } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { FieldError } from "@/components/ui/field-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  ITEM_STOCK_PATHS,
  tItemStock,
  type CountableItemOption,
} from "@/features/staff/item-stock/item-stock"

type FormShape = {
  item_id: string
  quantity: string
  low_threshold: string
}

/**
 * Starting to count a dish, with an explicit submit rather than an inline
 * field.
 *
 * Choosing *which* dish gets counted is a decision a manager makes once per
 * dish, not a light value retyped mid-shift — `quantity` and `low_threshold`
 * are the light fields, and they commit inline on the row this form's submit
 * creates (see `ItemStockWorkspace`). Opens in a drawer, mirroring
 * `SupplyForm`: the manager sets this up once per dish, not forty times a
 * shift, so it does not need to sit permanently on the page.
 */
export function ItemStockForm({
  countableItems,
  onDone,
}: {
  countableItems: CountableItemOption[]
  onDone: () => void
}) {
  const { data, setData, post, errors, processing } = useForm<FormShape>({
    item_id: "",
    quantity: "0",
    low_threshold: "0",
  })

  // `record.errors.to_hash(true)` keys a model-level error under `base`, which
  // is not a field on this form and so is absent from `useForm`'s inferred
  // error map — same read `SupplyForm` gives it. A refusal that is not about
  // one input (an item already counted by a race) must still surface.
  const baseError = (errors as Record<string, string | string[] | undefined>).base

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (data.item_id.trim().length === 0) return

    post(ITEM_STOCK_PATHS.itemStocks, { preserveScroll: true, onSuccess: () => onDone() })
  }

  return (
    <form className="space-y-4" onSubmit={submit} data-testid="item-stock-form">
      <FieldError error={baseError} />

      <div className="space-y-2">
        <Label htmlFor="item-stock-item">{tItemStock("item_label")}</Label>
        <Select value={data.item_id} onValueChange={(value) => setData("item_id", value)}>
          <SelectTrigger id="item-stock-item" className="w-full" data-testid="item-stock-item">
            <SelectValue placeholder={tItemStock("item_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {countableItems.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                data-testid={`item-stock-item-option-${option.value}`}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError error={errors.item_id} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="item-stock-quantity">{tItemStock("quantity_label")}</Label>
        <Input
          id="item-stock-quantity"
          inputMode="numeric"
          value={data.quantity}
          placeholder={tItemStock("quantity_placeholder")}
          onChange={(event) => setData("quantity", event.target.value)}
          data-testid="item-stock-form-quantity"
        />
        <FieldError error={errors.quantity} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="item-stock-low-threshold">{tItemStock("low_threshold_label")}</Label>
        <Input
          id="item-stock-low-threshold"
          inputMode="numeric"
          value={data.low_threshold}
          placeholder={tItemStock("low_threshold_placeholder")}
          onChange={(event) => setData("low_threshold", event.target.value)}
          data-testid="item-stock-form-threshold"
        />
        <p className="text-muted-foreground text-xs">{tItemStock("low_threshold_hint")}</p>
        <FieldError error={errors.low_threshold} />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone} data-testid="item-stock-form-cancel">
          {tItemStock("cancel")}
        </Button>
        <Button
          type="submit"
          disabled={processing || data.item_id.trim().length === 0}
          data-testid="item-stock-form-submit"
        >
          {processing ? tItemStock("creating") : tItemStock("create")}
        </Button>
      </div>
    </form>
  )
}
